import type { DayOfWeek } from 'common/models';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { getI18n } from 'react-i18next';

export const getNow = () => {
  return dayjs().utcOffset(0, true);
};

export const getMinutes = (time: Dayjs) => {
  const dayjsTime = dayjs(time);

  return dayjsTime.hour() * 60 + dayjsTime.minute();
};

export const getTimeFromMinutes = (minutes: number) => {
  return dayjs.utc().startOf('day').add(minutes, 'minutes');
};

export const getTimeFromMinutesFormated = (minutes: number) => {
  return dayjs.utc().startOf('day').add(minutes, 'minutes').format('LT');
};

export const getFirstDateOfWeek = (date: dayjs.Dayjs): dayjs.Dayjs => {
  const offset = date.startOf('week').day() - date.day();
  const fdowDate = date.add(offset, 'day');

  return fdowDate;
};

export const getDayOfWeekNumber = (day: DayOfWeek): number => {
  if (day === 'Sunday') return 0;
  if (day === 'Monday') return 1;
  if (day === 'Tuesday') return 2;
  if (day === 'Wednesday') return 3;
  if (day === 'Thursday') return 4;
  if (day === 'Friday') return 5;
  if (day === 'Saturday') return 6;

  return 0;
};

export const getStringFromMinutes = (minutes: number) => {
  const i18n = getI18n();
  return minutes < 60
    ? i18n.t('company.settings.schedule.minutesLength', { length: minutes })
    : i18n.t('company.settings.schedule.hoursLength', {
        length: minutes / 60
      });
};

export const getDatesInRange = (start: string, end: string) => {
  const currentDate = dayjs.utc(start).startOf('day');
  const endDate = dayjs.utc(end).startOf('day');

  const dates = [];

  while (currentDate.isSame(endDate) || currentDate.isBefore(endDate)) {
    dates.push(currentDate.clone().toISOString());
    currentDate.add(1, 'days');
  }

  return dates;
};

export const convertSToTime = (initTime: number) => {
  const hours = Math.floor(initTime / 3600);
  const minutes = Math.floor(initTime / 60) - hours * 60;
  const seconds = Math.floor(initTime % 60);

  return `${hours > 0 ? `${Math.floor(hours)}:` : '00:'}${minutes > 9 ? minutes : `0${minutes}`}:${
    seconds > 9 ? seconds : `0${seconds}`
  }`;
};

export const secondsToText = (initTime: number) => {
  const hours = Math.floor(initTime / 3600);
  const minutes = Math.ceil(initTime / 60) - hours * 60;

  return `${hours > 0 ? `${hours}h ` : ''}${minutes}min`;
};

export const renderInterventionDateTime = (
  interventionStartedAt?: string | null,
  interventionEndedAt?: string | null
) => {
  if (interventionStartedAt && !interventionEndedAt) {
    return renderLegacyDateTime(interventionStartedAt, 'l LT');
  }

  if (interventionStartedAt && interventionEndedAt) {
    if (dayjs.utc(interventionStartedAt).isSame(dayjs.utc(interventionEndedAt), 'day')) {
      return `${renderLegacyDateTime(interventionStartedAt, 'l LT')} - ${renderTime(
        interventionEndedAt
      )}`;
    }

    return `${renderLegacyDateTime(interventionStartedAt, 'l LT')} - ${renderLegacyDateTime(
      interventionEndedAt,
      'l LT'
    )}`;
  }

  return '';
};

/**
 * Converts a UTC ISO datetime string to a Dayjs object in the given IANA timezone.
 * Use this when displaying API v2 datetime values to the user.
 *
 * @example
 * utcToCompanyTz('2024-01-15T09:00:00Z', 'Europe/Prague')
 * // → Dayjs representing 2024-01-15 10:00 in Prague (UTC+1)
 */
export const utcToCompanyTz = (utcString: string, timezoneId: string): Dayjs => {
  const instant = dayjs.utc(utcString);
  if (!instant.isValid()) return instant;

  // Read the offset at the UTC instant so the two occurrences of a repeated hour stay distinct.
  const offset = new Intl.DateTimeFormat('en', {
    timeZone: timezoneId,
    timeZoneName: 'longOffset'
  })
    .formatToParts(instant.toDate())
    .find(({ type }) => type === 'timeZoneName')?.value;

  return instant.utcOffset(offset?.slice(3) || 0);
};

/**
 * Converts a Dayjs value (whose wall-clock time represents local company time) to a UTC ISO string.
 * Use this when sending datetime values to API v2 endpoints.
 *
 * @example
 * companyTzToUtc(dayjs('2024-01-15T10:00:00'), 'Europe/Prague')
 * // → '2024-01-15T09:00:00.000Z'
 */
export const companyTzToUtc = (value: Dayjs, timezoneId: string): string => {
  return dayjs.tz(value.format('YYYY-MM-DD HH:mm:ss'), timezoneId).utc().toISOString();
};

const formatWithoutTimezone = (
  dateTime: string | Dayjs | Date | null | undefined,
  format: string,
  locale = dayjs.locale()
): string => {
  if (!dateTime) return '';

  return dayjs.utc(dateTime).locale(locale).format(format);
};

/** Displays date and time without timezone conversion (V1 API). */
export const renderLegacyDateTime = (
  dateTime: string | Dayjs | Date | null | undefined,
  format = 'LLL',
  locale = dayjs.locale()
): string => {
  return formatWithoutTimezone(dateTime, format, locale);
};

/** Displays only the time portion, without timezone conversion. */
export const renderTime = (
  time: string | Dayjs | Date | null | undefined,
  format = 'LT',
  locale = dayjs.locale()
): string => {
  return formatWithoutTimezone(time, format, locale);
};

/** Calendar dates have no timezone; preserve the day received from the API. */
export const renderDate = (
  date: string | Dayjs | null | undefined,
  format = 'LL',
  locale = dayjs.locale()
): string => {
  return formatWithoutTimezone(date, format, locale);
};

/** V2 UTC instant: convert to the explicitly supplied company timezone. */
export const renderDateTimeInTimezone = (
  dateTime: string | null | undefined,
  timezoneId: string,
  format = 'LLL'
): string => {
  if (!dateTime) {
    return '';
  }

  return utcToCompanyTz(dateTime, timezoneId).format(format);
};

/** Displays only the time portion of a V2 UTC instant in the company timezone. */
export const renderTimeInTimezone = (
  time: string | null | undefined,
  timezoneId: string,
  format = 'LT'
): string => {
  if (!time) return '';

  return utcToCompanyTz(time, timezoneId).format(format);
};
